import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 't8-cart-v1';
const CartContext = createContext(null);

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function optionsKey(options = {}) {
  return Object.entries(options)
    .map(([k, v]) => `${k}:${v}`)
    .sort()
    .join('|');
}

function lineId(productId, options) {
  return `${productId}__${optionsKey(options)}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadCart());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const value = useMemo(() => {
    const count = items.reduce((sum, item) => sum + item.qty, 0);
    const subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);

    const addItem = (product, selected, price) => {
      const id = lineId(product.id, selected);
      const optionsLabel = Object.entries(selected)
        .map(([k, v]) => `${k}: ${v}`)
        .join('; ');

      setItems((prev) => {
        const existing = prev.find((item) => item.id === id);
        if (existing) {
          return prev.map((item) =>
            item.id === id ? { ...item, qty: item.qty + 1 } : item
          );
        }
        return [
          ...prev,
          {
            id,
            productId: product.id,
            name: product.name,
            image: product.image,
            options: { ...selected },
            optionsLabel,
            price,
            qty: 1,
          },
        ];
      });
    };

    const setQty = (id, qty) => {
      const next = Math.max(0, Math.min(99, Number(qty) || 0));
      setItems((prev) => {
        if (next <= 0) return prev.filter((item) => item.id !== id);
        return prev.map((item) => (item.id === id ? { ...item, qty: next } : item));
      });
    };

    const removeItem = (id) => setItems((prev) => prev.filter((item) => item.id !== id));
    const clearCart = () => setItems([]);

    const buildOrderMessage = () => {
      if (!items.length) return '';
      const lines = items.map(
        (item) =>
          `- ${item.name} × ${item.qty}${item.optionsLabel ? ` (${item.optionsLabel})` : ''} — $${(
            item.price * item.qty
          ).toFixed(2)}`
      );
      return [
        'I would like to order the following from my cart:',
        ...lines,
        `Cart total estimate: $${subtotal.toFixed(2)}`,
        'Please help me complete this purchase.',
      ].join('\n');
    };

    return {
      items,
      count,
      subtotal,
      addItem,
      setQty,
      removeItem,
      clearCart,
      buildOrderMessage,
    };
  }, [items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
